/**
 * SSR data-loading for SiteHome via Volto's `asyncPropsExtenders` hook.
 *
 * Content-type views (SiteHome included) are rendered by the generic View
 * container inside the catch-all content route — they are NOT distinct
 * react-router routes, so the classic per-route `asyncConnect([...])`
 * pattern (used by core's Search.jsx / Sitemap.jsx, which ARE their own
 * routes) does not apply here. The supported extension point for "extra SSR
 * data on an existing route without adding a new route" is
 * `config.settings.asyncPropsExtenders`: an array of
 * `{ path, extend(dispatchActions) }` entries that the top-level App
 * component's own asyncConnect consults on every route match (server AND
 * client — see helpers/AsyncConnect/index.js `applyExtenders` /
 * `matchAllRoutes`, keyed off react-router's `matchPath`). The one shipped
 * example in core is `getSiteAsyncPropExtender`
 * (helpers/Site/index.js, path: '/', unconditional match, pushes GET_SITE).
 *
 * This extender follows the exact same shape, scoped to the site root only
 * (`path: '/', exact: true`) so it never fires on any other route.
 *
 * NOT wired here — see the SiteHome build report for the exact index.ts
 * edit (`config.settings.asyncPropsExtenders = [...]`) this addon needs.
 *
 * BL-0152 root cause (found 2026-09-27): `path`/`exact` matching in core was
 * never the problem — `matchAllRoutes`/`matchPath` correctly exclude every
 * non-root path from calling `extend()` here. The bug was this function
 * mutating its `dispatchActions` argument in place with `.push()` and
 * returning that same reference. `applyExtenders` (core,
 * helpers/AsyncConnect/index.js) seeds its `reduce` with the App component's
 * own `asyncItems` array — a single object created once, at module load, and
 * reused as-is for every request for the life of the process (it is not
 * rebuilt per request). The very first request that legitimately matched `/`
 * pushed the five `home-*` items into THAT shared array permanently. Every
 * later request for any path then started `applyExtenders` from the same,
 * now-permanently-polluted array — `homeAsyncPropExtender.extend()` was
 * never called for those requests (path matching correctly excluded it), but
 * the five items were already baked into the base list before matching ever
 * ran, so `wrapWithDispatch` dispatched their promises unconditionally
 * regardless of the current route. Fix: return a new array instead of
 * mutating the one we were given, so the shared base list is never touched.
 */
import { searchContent } from '@plone/volto/actions';
import { siteIdFromEnv } from '../../siteConfig/branding';
import { buildHomeQueries, SUBREQUEST_NAMES } from './queries';

export const homeAsyncPropExtender = {
  path: '/',
  exact: true,
  extend: (dispatchActions) => {
    // Defensive de-dupe if this extender is ever consulted twice for the
    // same route match (mirrors the guard in getSiteAsyncPropExtender).
    if (dispatchActions.some((a) => a.key === 'home-added')) {
      return dispatchActions;
    }

    // Seamless mode: this running instance is bound to exactly one site via
    // RAZZLE_SITE_ID / RAZZLE_INTERNAL_API_PATH, identical on server and
    // client (see siteConfig/branding/index.ts siteIdFromEnv doc comment) —
    // safe to resolve here, outside of any React tree.
    const siteId = siteIdFromEnv() || 'standards';
    const queries = buildHomeQueries(siteId);

    // Do not mutate the array we were given — it is the shared base
    // `asyncItems` list App.jsx's asyncConnect was created with once, at
    // module load, not a fresh copy per request (see BL-0152 above). Pushing
    // onto it here would bake these five items into every future request's
    // starting point, on every route, for the life of the process.
    const homeItems = SUBREQUEST_NAMES.map((name) => ({
      key: `home-${name}`,
      promise: ({ store: { dispatch } }) =>
        dispatch(searchContent('', queries[name], `home-${name}`)),
    }));

    return [...dispatchActions, ...homeItems];
  },
};

export default homeAsyncPropExtender;
